import type { ReactNode } from "react";
import type { LinksFunction } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";

import styles from "./tailwind.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;500;700&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }): ReactNode {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="build-timestamp" />
        <style data-fullcalendar />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App(): ReactNode {
  return <Outlet />;
}

export default withSentry(App);

export function ErrorBoundary(): ReactNode {
  const error = useRouteError();

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("Error in error boundary", { error });
  }

  if (isRouteErrorResponse(error)) {
    return (
      <div className="min-h-screen bg-background">
        <main className="flex-1 p-6">
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </main>
      </div>
    );
  }

  captureRemixErrorBoundaryError(error);

  return <h1>Error!</h1>;
}
